import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BullhornNamespace, IBody, InveniasNamespace, SalesforceNamespace } from 'datasource-service-lib';
import { IDataSourceApiService } from 'interfaces-lib';
import { jwtDecode } from 'jwt-decode';
import { EMPTY, expand, forkJoin, Observable, of, reduce, throwError } from 'rxjs';
import { catchError, concatMap, map, mergeAll, switchMap, tap, toArray } from 'rxjs/operators';
import { Identity, IdentityStorage } from 'storage-service-lib';
import { UtilsService } from 'utils-service-lib';

export interface IApiListService {
  list(listParams: any): Observable<any[]>;
}

@Injectable({ providedIn: 'root' })
export class DocumentClientDatasourceApiListServiceFactory {
  public static instance: DocumentClientDatasourceApiListServiceFactory;

  constructor(private http: HttpClient, private identityStorage: IdentityStorage) {
    // set the singleton instance
    DocumentClientDatasourceApiListServiceFactory.instance = this;
  }

  create(namespace: string, datasourceApiService: IDataSourceApiService): IApiListService {
    if (namespace === InveniasNamespace.name) {
      return new InveniasApiListService(this.http, this.identityStorage);
    }

    if (namespace === BullhornNamespace.name) {
      return new BullhornApiListService(datasourceApiService, this.http);
    }

    if (namespace === SalesforceNamespace.name) {
      return new SalesforceApiListService(datasourceApiService, this.http);
    }

    throw new Error(`No api list service for '${namespace}'.`);
  }
}

class BullhornApiListService implements IApiListService {
  private static DEFAULT_SELECT = '';
  private static IDENTITY_MAPPER = (item) => item;
  private selectors: { [index: string]: any } = {
    fileAttachments: ['id', 'name', 'fileSize', 'dateAdded', 'type', 'contentSubType'],
  };
  private mappers: { [index: string]: (item) => any } = {
    fileAttachments: BullhornApiListService.fileAttachmentMapper,
  };

  constructor(private datasourceApiService: IDataSourceApiService, private http: HttpClient) {}

  list(listParams: any): Observable<any[]> {
    return this.retrieveRecords({ searchType: 'search', ...listParams });
  }

  private retrieveRecords(listParams: any): Observable<any> {
    const counterUrl = this.datasourceApiService.buildApiUrl(`${listParams.searchType}/${listParams.entity.type}`);
    const bodyCounter: IBody = {
      query: `id:${listParams.entity.id}`,
      fields: 'fileAttachments',
      count: 1,
    };
    const url = this.datasourceApiService.buildApiUrl(
      `entity/${listParams.entity.type}/${listParams.entity.id}/${listParams.associatedType}?fields=`
    );
    const selectFields = this.selectors[listParams.associatedType] || BullhornApiListService.DEFAULT_SELECT;
    const mapperFn = this.mappers[listParams.associatedType] || BullhornApiListService.IDENTITY_MAPPER;

    const buildObs = (first) => {
      const totalToPull = first?.data[0]?.fileAttachments?.total;
      const pageSize = 500;
      const pageCount = Math.ceil(totalToPull / pageSize);
      const obs: any[] = [];
      for (let i = 1; i <= pageCount; i++) {
        const start = (i - 1) * pageSize;
        const parameters = `${selectFields.toString()}&start=${start}&count=${pageSize}`;
        obs.push(this.http.get<any>(url + parameters));
      }
      return obs;
    };

    const first$ = this.http.post<any>(counterUrl, bodyCounter);
    return first$.pipe(
      map((first) => buildObs(first)),
      concatMap((obs) =>
        forkJoin(...obs).pipe(
          concatMap((r) => r.map((rr) => (rr as { data: any }).data)),
          mergeAll(),
          map((i) => mapperFn(i)),
          toArray()
        )
      ),
      tap((res) =>
        console.debug(`${res.length} ${listParams.associatedType} pulled for ${listParams.entity.type} ${listParams.entity.id}.`)
      )
    );
  }

  private static fileAttachmentMapper(item: any): any {
    const file: any = {
      id: item.id,
      name: item.name,
      size: item.fileSize,
      dateModified: item.dateAdded, // should this be date modified?
      dateAdded: item.dateAdded,
      contentSubType: item.contentSubType,
    };

    // we consider empty string and null as undefined
    if (item.type && item.type.length > 0) {
      file.documentType = { id: item.type, label: item.type };
    }

    return file;
  }
}

class InveniasApiListService implements IApiListService {
  private readonly inveniasApiUrl: string;
  private static DEFAULT_SELECT = {};
  private static IDENTITY_MAPPER = (item) => item;
  private selectors: { [index: string]: any } = {
    documents: {
      Select: ['ItemId', 'AttachmentName', 'DocumentSize', 'DateCreated', 'DateModified', 'ClientDocumentType', 'DocumentExtension'],
    },
  };
  private mappers: { [index: string]: (item) => any } = {
    documents: InveniasApiListService.documentMapper,
  };

  constructor(private http: HttpClient, private identityStorage: IdentityStorage) {
    this.inveniasApiUrl = InveniasApiListService.getUrlEndpoint((this.identityStorage.get() as Identity).token);
  }

  list(listParams: any): Observable<any[]> {
    const url = `${this.inveniasApiUrl}/${listParams.entity.type}/${listParams.entity.id}/${listParams.associatedType}/list`;

    let selectFields;
    if (listParams.fields) {
      selectFields = { Select: listParams.fields };
    } else {
      selectFields = this.selectors[listParams.associatedType] || InveniasApiListService.DEFAULT_SELECT;
    }
    const mapperFn = this.mappers[listParams.associatedType] || InveniasApiListService.IDENTITY_MAPPER;

    const buildObs = (first) => {
      const totalToPull = first.Paging.TotalItemCount;
      const pageSize = 100;
      const pageCount = Math.ceil(totalToPull / pageSize);
      const obs: any[] = [of(first)];
      for (let i = 1; i < pageCount; i++) {
        const body = { PageSize: pageSize, PageIndex: i, ...selectFields };
        obs.push(this.http.post<any>(url, body));
      }
      return obs;
    };

    const first$ = this.http.post<any>(url, selectFields);
    return first$.pipe(
      map((first) => buildObs(first)),
      concatMap((obs) =>
        forkJoin(...obs).pipe(
          concatMap((r) => r.map((rr) => (rr as { Items: any }).Items)),
          mergeAll(),
          map((i) => mapperFn(i)),
          toArray()
        )
      ),
      tap((res) =>
        console.debug(`${res.length} ${listParams.associatedType} pulled for ${listParams.entity.type} ${listParams.entity.id}.`)
      )
    );
  }

  private static documentMapper(item: any): any {
    const document: any = {
      id: item.ItemId,
      name: item.AttachmentName,
      size: item.DocumentSize,
      dateModified: new Date(item.DateModified).getTime(),
      fileExtension: item.DocumentExtension,
    };

    if (item.ClientDocumentType) {
      document.documentType = {
        id: item.ClientDocumentType?.Id,
        label: item.ClientDocumentType?.ItemDisplayText,
      };
    }

    return document;
  }

  private static getUrlEndpoint(token: string): string {
    const jwtPayload = jwtDecode<any>(token);
    const endpointUrl = jwtPayload['http://www.invenias.com/claims/EndpointUrl'];
    return `https://${endpointUrl}/api/v1`;
  }
}

export interface ISalesforceAttachment {
  id: string;
  name: string;
  size: number;
  dateAdded?: number;
  dateModified: number;
  type: string;
  fileExtension?: string;
  downloadUrl?: string;
  origin?: 'ContentDocument' | 'ContactDocument';
  contentType?: string;
  /** Populated for ContactDocuments — used to detect duplicates already represented as ContentDocuments. */
  contentVersionId?: string;
}

interface ISalesforceQueryResponse<TRecord> {
  done?: boolean;
  nextRecordsUrl?: string;
  records?: TRecord[];
}

interface ISalesforceContentDocumentLink {
  ContentDocument: ISalesforceContentDocument;
}

interface ISalesforceContentDocument {
  Id: string;
  Title: string;
  ContentSize: number;
  CreatedDate: string;
  LastModifiedDate: string;
  FileType: string;
  FileExtension?: string | null;
  LatestPublishedVersion?: { Id: string; PathOnClient?: string } | null;
}

interface ISalesforceContactDocument {
  Id: string;
  Name?: string | null;
  CreatedDate?: string | null;
  LastModifiedDate?: string | null;
  TR1__ContentVersionId__c: string | null;
  TR1__File_Size__c?: number | null;
  TR1__Type__c?: string | null;
  TR1__ContentVersionFileType__c?: string | null;
  TR1__ContentVersionFileExtension__c?: string | null;
  TKC_Formula_To_Retrieve_CVs_Resumes__c?: string | null;
  Formula_Function_To_Retrieve_CVs_Resumes__c?: string | null;
}

type SalesforceDocumentMapper = (doc: ISalesforceContentDocument) => ISalesforceAttachment;
type ContactDocumentFormulaField =
  | 'TKC_Formula_To_Retrieve_CVs_Resumes__c'
  | 'Formula_Function_To_Retrieve_CVs_Resumes__c';

export class SalesforceApiListService implements IApiListService {
  private static DEFAULT_SELECT = '';
  private static IDENTITY_MAPPER = (item) => item;
  /**
   * Ordered fallback for org-specific field names on TR1__ContactDocument__c.
   *
   * Important: SOQL cannot safely select both formula fields in one query because
   * selecting a missing column fails the whole request with INVALID_FIELD.
   *
   * Strategy:
   * 1) Query with the newer field name.
   * 2) If Salesforce reports INVALID_FIELD for that name, retry with legacy name.
   * 3) If still unavailable, query without formula field and use deterministic URL fallback.
   */
  private static CONTACT_DOCUMENT_FORMULA_FIELDS: (ContactDocumentFormulaField | null)[] = [
    'Formula_Function_To_Retrieve_CVs_Resumes__c',
    'TKC_Formula_To_Retrieve_CVs_Resumes__c',
    null, // safe fallback if both fields didn't exist.
  ];

  private selectors: { [index: string]: string[] } = {
    fileAttachments: ['Id', 'Title', 'ContentSize', 'CreatedDate', 'LastModifiedDate', 'FileType', 'FileExtension'],
  };

  // Map Salesforce ContentDocument to generic attachment
  private static fileAttachmentMapper: SalesforceDocumentMapper = (doc) => ({
    id: doc.Id,
    name: doc.Title, // will be set to the correct name with extension from ContentVersion leaving this as a fallback
    size: doc.ContentSize,
    dateAdded: Number.isFinite(Date.parse(doc.CreatedDate)) ? Date.parse(doc.CreatedDate) : undefined,
    dateModified: Date.parse(doc.LastModifiedDate),
    type: doc.FileType,
    origin: 'ContentDocument',
    fileExtension: doc.FileType,
    // downloadUrl to be filled later after fetching ContentVersionId
  })

  private mappers: Record<string, SalesforceDocumentMapper> = {
    fileAttachments: SalesforceApiListService.fileAttachmentMapper,
  };

  constructor(private datasourceApiService: IDataSourceApiService, private http: HttpClient) {}

  list(listParams: any): Observable<any[]> {
    return this.retrieveRecords(listParams);
  }

  private retrieveRecords(listParams: any): Observable<any[]> {
    const associatedType = listParams.associatedType || 'fileAttachments';
    const selectFields = this.selectors[associatedType] || [SalesforceApiListService.DEFAULT_SELECT];
    const mapperFn = this.mappers[associatedType] || SalesforceApiListService.IDENTITY_MAPPER;

    const recordId = listParams.entity.id;

    return forkJoin({
      contentDocs: this.getContentDocuments(recordId, selectFields),
      contactAttachments: this.getContactDocumentAttachments(recordId, listParams.entity.type),
    }).pipe(
      switchMap(({ contentDocs, contactAttachments }) => {
        if (!contentDocs.length) {
          return of(contactAttachments).pipe(
            tap((res) =>
              console.debug(
                `${res.length} ${associatedType} pulled for ${listParams.entity.type} ${listParams.entity.id}.`
              )
            )
          );
        }

        // Exclude ContentDocuments already represented as ContactDocuments.
        // For Contact entities, TR1__ContactDocument__c is the authoritative record:
        // its formula-based download URL works correctly, whereas querying ContentVersions
        // directly for Contact-linked ContentDocuments may fail due to Salesforce permissions.
        // A ContentDocument is a duplicate when its LatestPublishedVersion.Id matches a
        // ContactDocument's TR1__ContentVersionId__c (both point to the same file version).
        const coveredByContactDoc = new Set(
          contactAttachments.map((a) => a.contentVersionId).filter(Boolean)
        );
        const deduplicatedContentDocs = contentDocs.filter(
          (doc) => !doc.LatestPublishedVersion?.Id || !coveredByContactDoc.has(doc.LatestPublishedVersion.Id)
        );

        const contentAttachments = deduplicatedContentDocs.map((doc) =>
          this.mapContentDocumentRecord(doc, doc.LatestPublishedVersion ?? null, mapperFn)
        );

        return of([...contentAttachments, ...contactAttachments]).pipe(
          tap((res) =>
            console.debug(
              `${res.length} ${associatedType} pulled for ${listParams.entity.type} ${listParams.entity.id}.`
            )
          )
        );
      })
    );
  }

  private getContentDocuments(recordId: string, selectFields: string[]): Observable<ISalesforceContentDocument[]> {
    const prefixedFields = [
      ...selectFields.map((f) => `ContentDocument.${f}`),
      'ContentDocument.LatestPublishedVersion.Id',
      'ContentDocument.LatestPublishedVersion.PathOnClient',
    ].join(', ');
    const soql = `SELECT ${prefixedFields} FROM ContentDocumentLink WHERE LinkedEntityId = '${recordId}'`;
    const url = this.datasourceApiService.buildApiUrl(`/query/?q=${encodeURIComponent(soql)}`);

    return this.http.get<ISalesforceQueryResponse<ISalesforceContentDocumentLink>>(url).pipe(
      expand((response) => {
        if (response.nextRecordsUrl) {
          return this.http.get<ISalesforceQueryResponse<ISalesforceContentDocumentLink>>(
            this.datasourceApiService.buildApiUrl(response.nextRecordsUrl),
          );
        }
        return EMPTY;
      }),
      map((response) => response.records ?? []),
      reduce<ISalesforceContentDocumentLink[]>((all, page) => all.concat(page)),
      map((links) => {
        const seen = new Set<string>();
        return links.map((l) => l.ContentDocument).filter((doc) => doc && !seen.has(doc.Id) && seen.add(doc.Id));
      }),
    );
  }

  // Util: Download URL for file version
  private buildDownloadUrl(contentVersionId: string): string {
    return `/sobjects/ContentVersion/${contentVersionId}/VersionData`;
  }

  // Fallback to use ContactDocument download URL
  private buildContactDocumentDownloadUrl(contactDocumentId: string): string {
    return `/services/apexrest/TR1/api/v1/contact-documents/${contactDocumentId}/public-url`;
  }

  private getContactDocumentAttachments(recordId: string, entityType: string): Observable<ISalesforceAttachment[]> {
    if (entityType !== 'Contact') {
      return of([]);
    }

    // Start from preferred field; fallback chain is handled in the recursive method.
    // If TR1 package is not installed the whole observable falls back to [] so ContentDocuments
    // are used as the authoritative source instead.
    return this.getContactDocumentAttachmentsWithFormulaField(recordId, 0).pipe(
      catchError((error) => {
        if (this.isTr1PackageUnavailableError(error)) {
          console.warn('TR1__ContactDocument__c not available in this org — falling back to ContentDocuments only.', error);
          return of([]);
        }
        return throwError(() => error);
      }),
    );
  }

  private getContactDocumentAttachmentsWithFormulaField(
    recordId: string,
    formulaFieldIdx: number,
  ): Observable<ISalesforceAttachment[]> {
    const formulaField = SalesforceApiListService.CONTACT_DOCUMENT_FORMULA_FIELDS[formulaFieldIdx];
    // Build query with exactly one field candidate (or none), never both formula fields at once.
    const soqlContactDocs = this.buildContactDocumentSoql(recordId, formulaField);
    const contactDocsUrl = this.datasourceApiService.buildApiUrl(`/query/?q=${encodeURIComponent(soqlContactDocs)}`);

    return this.http
      .get<ISalesforceQueryResponse<ISalesforceContactDocument>>(contactDocsUrl)
      .pipe(
        map((response) => (response.records ?? []).map((record) => this.mapContactDocumentRecord(record))),
        catchError((error) => {
          // Retry only for schema drift (field missing). All other errors should propagate.
          const nextFormulaFieldIdx = formulaFieldIdx + 1;
          const hasNextFormulaField = nextFormulaFieldIdx < SalesforceApiListService.CONTACT_DOCUMENT_FORMULA_FIELDS.length;
          if (hasNextFormulaField && this.isMissingSalesforceFieldError(error, formulaField)) {
            // Move to next candidate in CONTACT_DOCUMENT_FORMULA_FIELDS.
            return this.getContactDocumentAttachmentsWithFormulaField(recordId, nextFormulaFieldIdx);
          }
          return throwError(() => error);
        }),
      );
  }

  private buildContactDocumentSoql(recordId: string, formulaField: ContactDocumentFormulaField | null): string {
    // Shared base fields expected in all orgs.
    const fields = [
      'Id',
      'Name',
      'CreatedDate',
      'LastModifiedDate',
      'TR1__ContentVersionId__c',
      'TR1__File_Size__c',
      'TR1__Type__c',
      'TR1__ContentVersionFileType__c',
      'TR1__ContentVersionFileExtension__c',
    ];

    if (formulaField) {
      // Include only the current fallback candidate for this attempt.
      fields.push(formulaField);
    }

    return `SELECT ${fields.join(', ')} FROM TR1__ContactDocument__c WHERE TR1__Contact__c = '${recordId}'`;
  }

  private isTr1PackageUnavailableError(error: unknown): boolean {
    // Salesforce returns INVALID_TYPE when the sObject doesn't exist in the org
    // (i.e. TR1 package not installed).
    const errors = (error as { error?: { errorCode?: string }[] })?.error;
    if (!Array.isArray(errors)) {
      return false;
    }
    return errors.some((sfError) => sfError?.errorCode === 'INVALID_TYPE');
  }

  private isMissingSalesforceFieldError(error: unknown, fieldName: ContactDocumentFormulaField | null): boolean {
    // Expected Salesforce error shape:
    // { error: [{ errorCode: 'INVALID_FIELD', message: 'No such column ...' }] }
    const errors = (error as { error?: { errorCode?: string; message?: string }[] })?.error;
    if (!Array.isArray(errors)) {
      return false;
    }

    return errors.some((sfError) => {
      if (sfError?.errorCode !== 'INVALID_FIELD') {
        return false;
      }
      if (!fieldName) {
        return true;
      }
      // Ensure fallback is triggered only when INVALID_FIELD references the current candidate.
      return typeof sfError?.message === 'string' && sfError.message.includes(fieldName);
    });
  }

  private mapContactDocumentRecord(record: ISalesforceContactDocument): ISalesforceAttachment {
    const name = record.Name?.trim() || 'Document';
    const sizeRaw = typeof record.TR1__File_Size__c === 'number'
      ? record.TR1__File_Size__c
      : Number(record.TR1__File_Size__c ?? 0);
    const size = Number.isFinite(sizeRaw) ? Math.max(0, Math.round(sizeRaw)) : 0;

    const parsedCreatedDate = record.CreatedDate ? Date.parse(record.CreatedDate) : NaN;
    const dateAdded = Number.isFinite(parsedCreatedDate) ? parsedCreatedDate : undefined;

    const dateSource = record.LastModifiedDate || record.CreatedDate || '';
    const parsedDate = dateSource ? Date.parse(dateSource) : NaN;
    const dateModified = Number.isFinite(parsedDate) ? parsedDate : null;

    const explicitExtension = record.TR1__ContentVersionFileExtension__c?.trim();
    const extensionFromName = this.extractExtension(record.Name);
    const extension = explicitExtension || extensionFromName || record.TR1__ContentVersionFileType__c || undefined;
    const normalizedExtension = extension ? extension.toLowerCase() : undefined;

    // URL precedence:
    // 1) New formula field value
    // 2) Legacy formula field value
    // 3) Hard fallback endpoint derived from ContactDocument Id
    const downloadUrl =
      record.Formula_Function_To_Retrieve_CVs_Resumes__c?.trim()
      || record.TKC_Formula_To_Retrieve_CVs_Resumes__c?.trim()
      || this.buildContactDocumentDownloadUrl(record.Id);

    const contentType =
      record.TR1__ContentVersionFileType__c?.trim()
      || (normalizedExtension ? UtilsService.extensionToMime(normalizedExtension.replace(/^\./, '')) : undefined);

    return {
      id: record.Id,
      name,
      size,
      dateAdded,
      dateModified,
      type: normalizedExtension?.replace(/^\./, ''),
      downloadUrl,
      origin: 'ContactDocument',
      contentType,
      fileExtension: normalizedExtension?.replace(/^\./, ''),
      contentVersionId: record.TR1__ContentVersionId__c ?? undefined,
    };
  }

  private extractExtension(name?: string | null): string | undefined {
    if (!name) {
      return undefined;
    }
    const idx = name.lastIndexOf('.');
    if (idx === -1 || idx === name.length - 1) {
      return undefined;
    }
    return name.substring(idx + 1);
  }

  private mapContentDocumentRecord(
    doc: ISalesforceContentDocument,
    contentVersion: { Id: string; PathOnClient?: string } | null,
    mapperFn: SalesforceDocumentMapper,
  ): ISalesforceAttachment {
    const attachment = mapperFn(doc);
    const pathName = contentVersion?.PathOnClient?.trim();
    const name = pathName || attachment.name;

    const extensionFromPath = this.extractExtension(pathName);
    const extension = extensionFromPath || doc.FileExtension || doc.FileType || attachment.fileExtension;
    const normalizedExtension = extension ? extension.toLowerCase() : undefined;

    return {
      ...attachment,
      name,
      type: normalizedExtension?.replace(/^\./, '') ?? attachment.type,
      fileExtension: normalizedExtension?.replace(/^\./, ''),
      contentType: normalizedExtension ?
        UtilsService.extensionToMime(normalizedExtension.replace(/^\./, '')) : attachment.contentType,
      downloadUrl: contentVersion
        ? this.buildDownloadUrl(contentVersion.Id)
        : attachment.downloadUrl,
    };
  }
}
